import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Dropdown, Listy, Popover, Table, Typography } from "antd";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { UserInfoContext } from "../FetchUserInfo";
import FilterRow from "../components/filter/FilterRow";
import { MoreOutlined, PlusOutlined } from "@ant-design/icons";
import useTableView from "../common/tableviews/TableViews";

const removeUndef = (obj) =>
  Object.entries(obj)
    .filter(([key, value]) => value !== undefined)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

const actions = (actionsMenu, refresh) => ({
  key: "actions",
  width: 70,
  fixed: "right",
  align: "center",
  render: (record) => (
    <div>
      <Dropdown
        trigger={["click"]}
        menu={{
          items: actionsMenu.items(record),
          onClick: ({ key, domEvent }) => {
            domEvent.stopPropagation();
            actionsMenu.onClick(key, record, refresh);
          },
        }}
      >
        <Button
          type="text"
          icon={<MoreOutlined />}
          size="small"
          onClick={(e) => e.stopPropagation()}
        />
      </Dropdown>
    </div>
  ),
});

const spToMap = (searchParams) =>
  [...searchParams.entries()].reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

const getKey = (record, rowKey) => (typeof rowKey === "function" ? rowKey(record) : record[rowKey]);

export default function ({
  fetchFunction,
  id,
  columns = [],
  rowKey = "id",
  defaultPage = 0,
  searchQueryName = "query",
  exportFn = () => {},
  defaultPageSize = 10,
  enableSelection = true,
  filterItems = [],
  addPath = "new",
  showAdd = true,
  addInTable = false,
  title = "",
  subtitle = "",
  children,
  actionsMenu = {},
  ...props
}) {
  const [loading, setLoading] = useState(true);
  const user = useContext(UserInfoContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState({});
  const page = searchParams.get("page") ?? defaultPage;
  const pageSize = searchParams.get("pageSize") ?? defaultPageSize;
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const location = useLocation();
  const [visibleColumns, setVisibleColumns] = useState([]);

  useEffect(() => {
    setVisibleColumns(columns.map((x) => ({ key: x.key, title: x.title, visible: true })));
  }, [columns]);

  const fetchData = () => {
    setLoading(true);
    setSelectedRowKeys([]);
    const params = spToMap(searchParams);
    fetchFunction({ ...params, page, pageSize }).then((data) => {
      setResult(data);
      setLoading(false);
    });
  };

  const queryParams = spToMap(searchParams);

  useEffect(() => {
    fetchData();
  }, [searchParams]);

  const onQueryParamsChange = (params) => {
    [...searchParams.keys()].forEach((k) => searchParams.delete(k));
    setSearchParams(removeUndef(params), { replace: true });
  };

  const finalColumns = columns
    .filter((c) => !!visibleColumns.filter((x) => c.key === x.key)[0]?.visible)
    .map((c) => ({
      ...c,
      sortOrder:
        c.key === searchParams.get("sortField") ? searchParams.get("sortOrder") : undefined,
    }));

  const handleSearch = (query) => {
    if (searchQueryName) onQueryParamsChange({ [searchQueryName]: query });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", rowGap: 16 }}>
      {title && (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <div>
            <Typography.Title level={3} style={{ marginBottom: 0, marginTop: 8 }}>
              {title}
            </Typography.Title>
            <Typography.Text type="secondary">{subtitle}</Typography.Text>
          </div>
          <div style={{ flex: 1, textAlign: "end" }}>
            {showAdd && !addInTable && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(addPath)}>
                Aggiungi
              </Button>
            )}
          </div>
        </div>
      )}
      {typeof children === "function" ? children?.(fetchData) : children}
      <div className="table-page">
        <FilterRow
          exportFn={exportFn}
          onChange={onQueryParamsChange}
          value={queryParams}
          id={id}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={setVisibleColumns}
          onSearch={handleSearch}
          extra={
            showAdd &&
            addInTable && (
              <Button type={"primary"} icon={<PlusOutlined />} onClick={() => navigate(addPath)}>
                Aggiungi
              </Button>
            )
          }
          items={{
            filter: filterItems,
            columns: columns,
            sorting: columns,
          }}
          style={{ padding: 16 }}
          columns={columns}
        />
        <div>
          <Table
            columns={
              actionsMenu.items ? [...finalColumns, actions(actionsMenu, fetchData)] : finalColumns
            }
            tableLayout={"fixed"}
            rowKey={rowKey}
            size={"small"}
            dataSource={result?.data?.map((x) => ({ ...x, key: x.id }))}
            sticky
            loading={loading}
            onChange={(pagin, filters, sort) => {
              searchParams.delete("sortField");
              searchParams.delete("sortOrder");
              if (sort.order && (sort.columnKey ?? sort.field)) {
                searchParams.set("sortField", sort.columnKey ?? sort.field);
                searchParams.set("sortOrder", sort.order);
              }
              searchParams.set("page", pagin.current - 1);
              searchParams.set("pageSize", pagin.pageSize);
              setSearchParams(searchParams);
            }}

            onRow={(record) => ({
              onClick: () => {
                setSelectedRowKeys((old) =>
                  old.includes(getKey(record, rowKey))
                    ? old.filter((x) => x !== getKey(record, rowKey))
                    : [...old, getKey(record, rowKey)],
                );
              },
            })}
            rowSelection={
              enableSelection && {
                fixed: "left",
                columnWidth: 50,
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                type: "checkbox",
              }
            }
            pagination={{
              current: Number(page) + 1,
              showTotal: (total, range) => (
                <Typography.Text type="secondary">
                  {range[0]}-{range[1]} di {total} elementi
                </Typography.Text>
              ),
              pageSize: pageSize,
              total: result.count ?? 0,
              showSizeChanger: true,
            }}
            scroll={{ x: finalColumns.reduce((acc, col) => col.width ?? 0 + acc, 0) }}
            styles={{ pagination: { root: { padding: 16, margin: 0 } } }}
          />
        </div>
      </div>
    </div>
  );
}
